import { MIN_PERCENTAGE } from "@constants";
import {
  AccountType,
  CalculateAnnualDepositProps,
  CalculateInitialBalanceProps,
  CalculateTotalPartnerAmountInCentsProps,
  ConvertToAnnualAmountProps,
  GetGoalProps,
  GetSipsFromBeneficiaryProps,
  Goal,
  GoalStoreProps,
  Maybe,
  ReceivedShareGoalInvite,
  Sip,
  SipFrequency,
  SipStatus,
} from "@ctypes";
import dayjs from "dayjs";
import { convertCentsToDollar, convertToMultiplier } from "./formatter";

/** Active recurring deposits only — a paused/cancelled/on-hold sip must
    never count toward a contribution total. */
export const getSipsFromBeneficiary = ({ beneficiary }: GetSipsFromBeneficiaryProps) => {
  const sips: Array<Sip> = [];

  beneficiary?.goals?.forEach((goal) => {
    const goalSips = goal?.sips ?? [];

    if (goalSips.length > 0) {
      sips.push(...(goalSips as Array<Sip>));
    }
  });

  return sips.filter((sip) => sip.status === SipStatus.Active);
};

/** Weekly-equivalent total of active recurring deposits for a beneficiary, in dollars. */
export const getWeeklyRecurringTotal = ({ beneficiary }: GetSipsFromBeneficiaryProps) =>
  getSipsFromBeneficiary({ beneficiary }).reduce((sum, sip) => {
    const dollars = convertCentsToDollar(sip.recurringDepositInCents);
    if (sip.recurringDepositSchedule === SipFrequency.Monthly) {
      return sum + (dollars * 12) / 52;
    }
    if (sip.recurringDepositSchedule === SipFrequency.Annually) {
      return sum + dollars / 52;
    }
    if (sip.recurringDepositSchedule === SipFrequency.Daily) {
      return sum + (dollars * 365) / 52;
    }
    return sum + dollars;
  }, 0);

export const convertToAnnualAmount = ({
  recurringDepositInCents = 0,
  frequency = SipFrequency.Weekly,
}: ConvertToAnnualAmountProps) => {
  if (recurringDepositInCents == null) return 0;

  if (frequency === SipFrequency.Weekly) {
    return recurringDepositInCents * 52;
  } else if (frequency === SipFrequency.Monthly) {
    return recurringDepositInCents * 12;
  } else if (frequency === SipFrequency.Annually) {
    return recurringDepositInCents;
  } else if (frequency === SipFrequency.Daily) {
    return recurringDepositInCents * 365;
  }

  return 0;
};

export const calculateAnnualDeposit = ({
  goal,
  skipPartner = false,
}: CalculateAnnualDepositProps) => {
  let total = 0;

  goal?.sips?.forEach((sip) => {
    if (sip) {
      total += convertToAnnualAmount({
        recurringDepositInCents: sip.recurringDepositInCents,
        frequency: sip.recurringDepositSchedule,
      });
    }
  });

  if (!skipPartner) {
    goal?.partners?.forEach((partner) => {
      if (partner) {
        total += convertToAnnualAmount({
          recurringDepositInCents: partner.recurringDepositInCents,
          frequency: partner.recurringDepositSchedule,
        });
      }
    });
  }

  return convertCentsToDollar(total);
};

export const calculateInitialBalance = ({ goal }: CalculateInitialBalanceProps) => {
  let total = goal?.currentAmountInCents ?? 0;

  goal?.partners?.forEach((partner) => {
    if (partner) {
      total += partner.contributionsInCents ?? 0;
    }
  });

  return convertCentsToDollar(total);
};

export const getHarvestAddGoalVariables = ({
  accountType,
  harvestStandardDeduction,
  harvestLongTermCapitalGains,
  harvestRecommendedFloorUsd,
}: Pick<
  GoalStoreProps,
  | "accountType"
  | "harvestStandardDeduction"
  | "harvestLongTermCapitalGains"
  | "harvestRecommendedFloorUsd"
>) => {
  if (accountType !== AccountType.HarvestUtmaUgma) return {};

  return {
    harvestStandardDeduction,
    harvestLongTermCapitalGains,
    harvestRecommendedFloorUsd,
  };
};

export const getGoal = ({ beneficiaryId, goalId, beneficiaries }: GetGoalProps) => {
  if (!beneficiaries?.length || !beneficiaryId || !goalId) {
    return null;
  }

  const beneficiary = beneficiaries?.find((b) => b?.id === beneficiaryId);
  const goal = beneficiary?.goals?.find((g) => g?.id === goalId);

  return goal;
};

export const calculateMaxGoalLength = (goals?: Array<Maybe<Goal>> | null) => {
  let maxGoalLength = 0;

  goals?.forEach((g) => {
    const startYear = dayjs(g?.startDate).year();
    const endYear = dayjs(g?.endDate).year();

    const goalLengthInYears = endYear - startYear;

    if (goalLengthInYears > maxGoalLength) {
      maxGoalLength = goalLengthInYears;
    }
  });

  return maxGoalLength;
};

export const calculateTotalAnnualBeneficiaryDeposit = (goals?: Array<Maybe<Goal>> | null) => {
  let annualDeposit = 0;

  goals?.forEach((g) => {
    annualDeposit += calculateAnnualDeposit({ goal: g });
  });

  return annualDeposit;
};

export const calculateMaxMultiplier = (goals?: Array<Maybe<Goal>> | null) => {
  let rateOfReturn = MIN_PERCENTAGE;

  goals?.forEach((g) => {
    if (g?.projectedRateOfReturn) {
      if (g.projectedRateOfReturn > rateOfReturn) {
        rateOfReturn = g.projectedRateOfReturn;
      }
    }
  });

  return convertToMultiplier(rateOfReturn);
};

export const calculateContributeAfter18Value = (goals?: Array<Maybe<Goal>> | null) => {
  goals?.forEach((g) => {
    if (g?.contributionsAfter18) {
      return true;
    }
  });
  return false;
};

export const calculateTotalGoalsTargetAmount = (goals?: Array<Maybe<Goal>> | null) => {
  let totalAmountInCents = 0;

  goals?.forEach((g) => {
    if (g?.targetAmountInCents) {
      totalAmountInCents += g.targetAmountInCents;
    }
  });

  return convertCentsToDollar(totalAmountInCents);
};

export const calculateTotalInviteAmountInCents = (pendingInvite: ReceivedShareGoalInvite) => {
  let amount = pendingInvite.recurringDepositInCents ?? 0;

  pendingInvite.partnerGoalContributions?.forEach((contribution) => {
    amount += contribution?.recurringDepositInCents ?? 0;
  });

  return amount;
};

export const calculateTotalPartnerAmountInCents = ({
  goal,
}: CalculateTotalPartnerAmountInCentsProps) => {
  let total = 0;

  goal?.partners?.forEach((partner) => {
    if (partner) {
      total += partner.recurringDepositInCents ?? 0;
    }
  });

  return total;
};
